import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/common/Page";
import { Badge } from "@/pages/legal/legalConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Wand2, Save, AlertTriangle, MessageSquarePlus, UploadCloud, ShieldCheck } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500";
const today = () => new Date().toISOString().slice(0, 10);
const LIFECYCLE = ["Draft", "Internal Review", "Sent to Customer", "Negotiation", "Awaiting Signature", "Executed", "Active", "Expiring", "Expired", "Terminated", "Superseded", "Archived"];
const lifecycleColor = { Executed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", Active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", Draft: "bg-slate-500/15 text-slate-300 border-slate-500/30" };

const COMMERCIAL_FIELDS = [
  ["subscription_fee", "Base Subscription"], ["implementation_fee", "Implementation Fee"],
  ["per_vessel_fee", "Per-Vessel Fee"], ["additional_user_fee", "Additional User Fee"],
  ["storage_allowance", "Included Storage"], ["excess_storage_rate", "Excess Storage Rate"],
  ["ai_allowance", "AI Allowance"], ["excess_ai_rate", "Excess AI Rate"],
  ["development_rate", "Development Rate"], ["payment_terms", "Payment Terms"],
  ["gst_treatment", "GST Treatment"],
];
const DATE_FIELDS = [["effective", "Effective"], ["commencement", "Commencement"], ["expiry", "Expiry"], ["renewal", "Renewal"]];

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [commercial, setCommercial] = useState(null);
  const [dates, setDates] = useState(null);
  const [notice, setNotice] = useState("");
  const [special, setSpecial] = useState("");
  const [note, setNote] = useState("");

  const { data: c } = useQuery({
    queryKey: ["legal-contract", id],
    queryFn: async () => {
      const d = (await api.get(`/legal/contracts/${id}`)).data;
      setCommercial(d.commercial || {}); setDates(d.dates || {});
      setNotice(String(d.legal_meta?.notice_period_days ?? "")); setSpecial(d.special_conditions || "");
      return d;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["legal-contract", id] });

  const save = useMutation({
    mutationFn: async () => (await api.put(`/legal/contracts/${id}`, {
      commercial, dates, special_conditions: special,
      legal_meta: { ...(c.legal_meta || {}), notice_period_days: notice ? Number(notice) : null },
    })).data,
    onSuccess: () => { refresh(); toast.success("Contract saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const setStatus = useMutation({
    mutationFn: async (status) => (await api.post(`/legal/contracts/${id}/status`, { status })).data,
    onSuccess: () => { refresh(); toast.success("Status updated"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const addNote = useMutation({
    mutationFn: async () => (await api.put(`/legal/contracts/${id}`, { negotiation_note: note })).data,
    onSuccess: () => { setNote(""); refresh(); toast.success("Negotiation note added"); },
  });
  const generate = useMutation({
    mutationFn: async () => (await api.post(`/legal/contracts/${id}/generate`)).data,
    onSuccess: (r) => {
      refresh();
      if (r.missing_variables?.length) toast.warning(`Generated with ${r.missing_variables.length} missing field(s): ${r.missing_variables.join(", ")}`);
      else toast.success("Draft document generated");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const [execOpen, setExecOpen] = useState(false);
  const [exec, setExec] = useState({ signatories: "", execution_date: today(), effective_date: "", notes: "", file: null });
  const doExecute = useMutation({
    mutationFn: async () => {
      const sigs = exec.signatories.split("\n").map((l) => l.trim()).filter(Boolean)
        .map((l) => { const [name, title] = l.split(",").map((x) => x.trim()); return { name, title: title || "" }; });
      const fd = new FormData();
      fd.append("file", exec.file);
      fd.append("signatories", JSON.stringify(sigs));
      fd.append("execution_date", exec.execution_date || "");
      fd.append("effective_date", exec.effective_date || "");
      fd.append("notes", exec.notes || "");
      return (await api.post(`/legal/contracts/${id}/execute`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: () => { refresh(); setExecOpen(false); toast.success("Contract executed — record is now immutable"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const openDoc = async (docId) => {    const res = await api.get(`/documents/${docId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  if (!c || !commercial) return null;
  const executed = c.lifecycle_status === "Executed";

  return (
    <div data-testid="contract-detail">
      <button onClick={() => navigate("/legal/contracts")} className="mb-3 flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300" data-testid="back-to-contracts"><ArrowLeft className="h-4 w-4" /> Contracts</button>
      <PageHeader title={`${c.contract_ref} — ${c.customer_legal_name}`} subtitle={`${c.contract_type}${c.product_name ? " · " + c.product_name : ""}`}>
        <Badge value={c.lifecycle_status} map={lifecycleColor} testId="contract-lifecycle-badge" />
        <Select value={c.lifecycle_status} onValueChange={(v) => setStatus.mutate(v)}>
          <SelectTrigger className="h-9 w-48 border-[#2D3F62] bg-[#0B1120] text-xs text-slate-100" data-testid="contract-status-select"><SelectValue /></SelectTrigger>
          <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">{LIFECYCLE.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {executed && <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200" data-testid="executed-lock"><AlertTriangle className="h-4 w-4" /> Executed — immutable. Corrections require an amendment/variation.</div>}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4 lg:col-span-2">
          <SectionTitle className="mb-3">Commercial Terms</SectionTitle>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {COMMERCIAL_FIELDS.map(([k, l]) => (
              <div key={k}><Label className="text-[11px] text-slate-500">{l}</Label>
                <Input disabled={executed} value={commercial[k] ?? ""} onChange={(e) => setCommercial({ ...commercial, [k]: e.target.value })} className={`mt-1 ${inputCls}`} data-testid={`commercial-${k}`} /></div>
            ))}
          </div>
          <SectionTitle className="mb-3 mt-5">Dates & Renewal</SectionTitle>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {DATE_FIELDS.map(([k, l]) => (
              <div key={k}><Label className="text-[11px] text-slate-500">{l}</Label>
                <Input type="date" disabled={executed} value={dates[k] ?? ""} onChange={(e) => setDates({ ...dates, [k]: e.target.value })} className={`mt-1 ${inputCls}`} data-testid={`date-${k}`} /></div>
            ))}
            <div><Label className="text-[11px] text-slate-500">Notice period (days)</Label>
              <Input type="number" disabled={executed} value={notice} onChange={(e) => setNotice(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="notice-period-input" /></div>
          </div>
          {c.dates?.notice_deadline && <p className="mt-2 text-xs text-amber-300" data-testid="notice-deadline">Non-renewal notice deadline: {c.dates.notice_deadline}</p>}
          <div className="mt-4"><Label className="text-[11px] text-slate-500">Special Conditions</Label>
            <Textarea rows={3} disabled={executed} value={special} onChange={(e) => setSpecial(e.target.value)} className={`mt-1 ${inputCls}`} data-testid="special-conditions-input" /></div>
          {!executed && <Button onClick={() => save.mutate()} disabled={save.isPending} className="mt-4 bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="save-contract-btn"><Save className="mr-1.5 h-4 w-4" /> Save</Button>}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
            <SectionTitle className="mb-3">Document</SectionTitle>
            <p className="mb-2 text-xs text-slate-500">Template: {c.template_id ? `${c.template_version}` : "none linked"}</p>
            <Button onClick={() => generate.mutate()} disabled={generate.isPending || !c.template_id || executed} className="w-full bg-gradient-to-r from-emerald-500 to-cyan-500 font-semibold text-slate-900" data-testid="generate-doc-btn"><Wand2 className="mr-1.5 h-4 w-4" /> Generate Draft</Button>
            {!executed && <Button onClick={() => setExecOpen(true)} variant="outline" className="mt-2 w-full border-emerald-500/30 bg-transparent text-emerald-300 hover:bg-emerald-500/10" data-testid="execute-open-btn"><UploadCloud className="mr-1.5 h-4 w-4" /> Upload Signed & Execute</Button>}
            {executed && <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-300" data-testid="executed-info"><ShieldCheck className="h-3.5 w-3.5" /> Executed & locked{c.dates?.signed ? ` · signed ${String(c.dates.signed).slice(0, 10)}` : ""}</p>}
            <div className="mt-3 space-y-1.5" data-testid="contract-documents">
              {(c.documents || []).map((d) => (
                <button key={d.document_id} onClick={() => openDoc(d.document_id)} className="flex w-full items-center gap-2 rounded-md border border-[#2D3F62] px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-[#17233B]" data-testid={`contract-doc-${d.document_id}`}>
                  <FileDown className="h-3.5 w-3.5 text-cyan-400" /> {d.filename}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-[#1E2D4A] bg-[#111A2E]/70 p-4">
            <SectionTitle className="mb-3">Negotiation Notes</SectionTitle>
            <div className="flex gap-2">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add note / redline summary" className={inputCls} data-testid="negotiation-note-input" />
              <Button size="icon" onClick={() => addNote.mutate()} disabled={!note} className="bg-cyan-600 text-slate-900" data-testid="add-note-btn"><MessageSquarePlus className="h-4 w-4" /></Button>
            </div>
            <div className="mt-3 space-y-1.5">
              {(c.negotiation || []).map((n, i) => (
                <div key={i} className="rounded-md border border-[#1E2D4A] bg-[#0B1120] px-2 py-1.5 text-xs text-slate-300">{n.note}<span className="mt-0.5 block text-[10px] text-slate-500">{n.by} · {new Date(n.at).toLocaleDateString()}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={execOpen} onOpenChange={setExecOpen}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-lg" data-testid="execute-dialog">
          <DialogHeader><DialogTitle>Upload Signed & Execute</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <p className="text-xs text-amber-300">Marking Executed locks this contract permanently. A SHA-256 checksum of the signed file is stored for integrity.</p>
            <div><Label className="text-xs text-slate-400">Signed document (PDF or DOCX)</Label>
              <Input type="file" accept=".pdf,.docx,.doc" onChange={(e) => setExec({ ...exec, file: e.target.files?.[0] })} className={`mt-1 ${inputCls}`} data-testid="execute-file-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-400">Execution date</Label><Input type="date" value={exec.execution_date} onChange={(e) => setExec({ ...exec, execution_date: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="execute-date-input" /></div>
              <div><Label className="text-xs text-slate-400">Effective date</Label><Input type="date" value={exec.effective_date} onChange={(e) => setExec({ ...exec, effective_date: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="execute-effective-input" /></div>
            </div>
            <div><Label className="text-xs text-slate-400">Signatories (one per line: Name, Title)</Label>
              <Textarea rows={3} value={exec.signatories} onChange={(e) => setExec({ ...exec, signatories: e.target.value })} placeholder={"M Almeida, Director\nClient Rep, CEO"} className={`mt-1 ${inputCls}`} data-testid="execute-signatories-input" /></div>
            <div><Label className="text-xs text-slate-400">Notes</Label><Input value={exec.notes} onChange={(e) => setExec({ ...exec, notes: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="execute-notes-input" /></div>
          </div>
          <DialogFooter>
            <Button onClick={() => doExecute.mutate()} disabled={doExecute.isPending || !exec.file} className="bg-gradient-to-r from-emerald-500 to-cyan-500 font-semibold text-slate-900" data-testid="execute-confirm">Execute & Lock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
